/**
 * O seletor e o campo de data COM CAPA do portal (PK-09) — o `dv-select` e o `dv-date` dos
 * protótipos 11.x, no desenho que o app interno já usa (`SeletorDoSistema`, `CampoDeData`).
 *
 * O controle NATIVO continua sendo o controle: fica onde sempre esteve, com o mesmo valor, o
 * mesmo `onChange`, o mesmo nome acessível e a mesma ordem de tabulação. Em tela larga com
 * ponteiro fino ele fica transparente e por cima dele vai uma capa desenhada; no toque a capa
 * some e a roda nativa volta, que é o melhor seletor que existe num telefone. O teclado e o
 * leitor de tela falam com o nativo; o `selectOption` dos testes também.
 *
 * O componente é do portal, e não importado do app interno, de propósito: o portal é outra SPA,
 * com outra folha e sem Tailwind (DS-520). A regra é a mesma; o componente é de cada SPA.
 */
import {
  Children,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { Calendar, Check, ChevronDown } from 'lucide-react'

const TELA_LARGA_COM_PONTEIRO_FINO = '(min-width: 768px) and (pointer: fine)'

function useDecora(): boolean {
  const [decora, setDecora] = useState(() => window.matchMedia(TELA_LARGA_COM_PONTEIRO_FINO).matches)
  useEffect(() => {
    const consulta = window.matchMedia(TELA_LARGA_COM_PONTEIRO_FINO)
    const aoMudar = (): void => {
      setDecora(consulta.matches)
    }
    consulta.addEventListener('change', aoMudar)
    return () => {
      consulta.removeEventListener('change', aoMudar)
    }
  }, [])
  return decora
}

function textoDe(no: ReactNode): string {
  if (typeof no === 'string') return no
  if (typeof no === 'number') return String(no)
  if (Array.isArray(no)) return no.map((parte) => textoDe(parte as ReactNode)).join('')
  return ''
}

export function SeletorComCapa({
  children,
  className = '',
  larguraPeloValor = false,
  ...resto
}: SelectHTMLAttributes<HTMLSelectElement> & {
  /**
   * A capa mede o RÓTULO escolhido, e não a opção mais longa do nativo: é o botão de período do
   * app interno, que cresce com o valor (filtro-periodo.tsx:151-159; P35 · DS-531). O nativo
   * continua por baixo, do mesmo tamanho da capa, e segue sendo o controle.
   */
  larguraPeloValor?: boolean
}) {
  const decora = useDecora()
  const [lista, setLista] = useState<{ topo: number; esquerda: number; largura: number } | null>(null)
  const nativo = useRef<HTMLSelectElement>(null)
  const capa = useRef<HTMLDivElement>(null)

  const opcoes: { valor: string; rotulo: string }[] = []
  for (const filho of Children.toArray(children)) {
    if (!isValidElement(filho) || filho.type !== 'option') continue
    const props = filho.props as { value?: string | number; children?: ReactNode }
    // Sem `value`, o valor da opção é o próprio texto — é o que o navegador faz.
    opcoes.push({ valor: String(props.value ?? textoDe(props.children)), rotulo: textoDe(props.children) })
  }
  const valor = String(resto.value ?? '')

  useEffect(() => {
    if (lista === null) return undefined
    const fechar = (): void => {
      setLista(null)
    }
    const fora = (evento: MouseEvent): void => {
      if (evento.target instanceof Node && capa.current?.parentElement?.contains(evento.target) === true) return
      setLista(null)
    }
    const escape = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return
      // A lista consome o Escape: fechar o seletor não fecha o diálogo que o contém.
      evento.preventDefault()
      evento.stopPropagation()
      setLista(null)
      nativo.current?.focus()
    }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', escape, true)
    window.addEventListener('resize', fechar)
    window.addEventListener('scroll', fechar, true)
    return () => {
      document.removeEventListener('mousedown', fora)
      document.removeEventListener('keydown', escape, true)
      window.removeEventListener('resize', fechar)
      window.removeEventListener('scroll', fechar, true)
    }
  }, [lista])

  /*
    A lista é `position: fixed`, medida na abertura: o seletor mora dentro de um painel com
    `overflow: hidden`, que cortaria uma lista absoluta. Rolar ou redimensionar a fecha.
  */
  const abrir = (): void => {
    if (resto.disabled === true) return
    if (lista !== null) {
      setLista(null)
      return
    }
    const caixa = capa.current?.getBoundingClientRect()
    if (caixa === undefined) return
    setLista({ topo: caixa.bottom + 4, esquerda: caixa.left, largura: caixa.width })
  }

  // A escrita passa pelo ELEMENTO: o React vê o mesmo `change` do seletor nativo.
  const escolher = (novo: string): void => {
    setLista(null)
    const no = nativo.current
    if (no === null) return
    Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set?.call(no, novo)
    no.dispatchEvent(new Event('change', { bubbles: true }))
  }

  return (
    <span className={decora ? `capa-controle decorado${larguraPeloValor ? ' pelo-valor' : ''}` : 'capa-controle'}>
      <select {...resto} ref={nativo} className={`${className}${decora ? ' nativo-sob-capa' : ''}`}>
        {children}
      </select>
      {decora && (
        <div aria-hidden="true" role="button" ref={capa} className="capa" data-teste="capa-do-seletor" onClick={abrir}>
          <span className="capa-valor">{opcoes.find((o) => o.valor === valor)?.rotulo ?? ''}</span>
          <ChevronDown size={16} />
        </div>
      )}
      {decora && lista !== null && (
        <ul
          aria-hidden="true"
          className="capa-lista"
          style={{ top: lista.topo, left: lista.esquerda, minWidth: Math.max(lista.largura, 180) }}
        >
          {opcoes.map((o) => (
            <li
              key={o.valor}
              aria-selected={o.valor === valor}
              onClick={() => {
                escolher(o.valor)
              }}
            >
              <span>{o.rotulo}</span>
              {o.valor === valor && <Check size={16} />}
            </li>
          ))}
        </ul>
      )}
    </span>
  )
}

/** `2026-07-01` → `01/07/2026`; vazio fica vazio. */
function dataNaCapa(iso: string): string {
  const [ano, mes, dia] = iso.split('-')
  return ano === undefined || mes === undefined || dia === undefined ? '' : `${dia}/${mes}/${ano}`
}

/**
 * O campo de data com capa (o `dv-date` do 11.1:517): a capa mostra a data e o ícone; o clique
 * abre o calendário do próprio navegador (`showPicker`), sobre o `<input type="date">` que
 * continua sendo o controle.
 */
export function CampoDeDataComCapa({ className = '', ...resto }: InputHTMLAttributes<HTMLInputElement>) {
  const decora = useDecora()
  const nativo = useRef<HTMLInputElement>(null)
  return (
    <span className={decora ? 'capa-controle decorado' : 'capa-controle'}>
      <input {...resto} type="date" ref={nativo} className={`${className}${decora ? ' nativo-sob-capa' : ''}`} />
      {decora && (
        <div
          aria-hidden="true"
          role="button"
          className="capa capa-data"
          data-teste="capa-da-data"
          onClick={() => {
            const campo = nativo.current
            if (campo === null) return
            campo.focus()
            try {
              campo.showPicker()
            } catch {
              // Sem `showPicker` (ou sem gesto), o foco no nativo já deixa o teclado escrever a data.
            }
          }}
        >
          <span className="capa-valor tnum">{dataNaCapa(String(resto.value ?? ''))}</span>
          <Calendar size={14} />
        </div>
      )}
    </span>
  )
}
